/**
 * Category helpers for Platform Reports Hub.
 *
 * Categories mirror the canonical exception-domain list in
 * `EXCEPTION_DOMAIN_ORDER` / `EXCEPTION_DOMAIN_META`. Backend `CATEGORY`
 * strings on each report module must match these labels exactly.
 *
 * Each individual exception type from the umbrella `exceptions` report is
 * expanded into its own synthetic hub row via `buildExceptionRows`, deep-linking
 * to the exceptions page with `?exceptionTypes=<code>` so both the Domain and
 * Exception Type dropdowns pre-select on arrival.
 */
import React from 'react'
import { Receipt as FallbackIcon, QueryStats as AnalysisIcon } from '@mui/icons-material'
import { ReportType } from '../../../../services/platformReportsService'
import {
  EXCEPTION_DOMAIN_META,
  EXCEPTION_DOMAIN_ORDER,
  ExceptionDomainIcon,
} from '../../../../components/SystemIcons'
import type { ExceptionDataDomain } from '../../../../types/exceptions'

export const CATEGORY_ORDER: string[] = EXCEPTION_DOMAIN_ORDER.map(
  (d) => EXCEPTION_DOMAIN_META[d].label,
)

/**
 * "Analysis" report appearance (a report kind, `!is_exception_report`, not an
 * exception type - so it lives here rather than in `EXCEPTION_TYPE_META`).
 * Single source for both the type-toggle "Analysis" segment and the per-row
 * pill: soft blue fill with the QueryStats glyph.
 */
export const ANALYSIS_META = {
  label: 'Analysis',
  color: '#1565c0',
  bgColor: '#e3f2fd',
  icon: AnalysisIcon,
} as const

export const categoryToSlug = (category: string): string =>
  category.toLowerCase().replace(/ & /g, '-').replace(/\s+/g, '-')

export const slugToCategory = (slug: string): string | undefined =>
  CATEGORY_ORDER.find((c) => categoryToSlug(c) === slug)

export const DOMAIN_TO_GROUP: Record<ExceptionDataDomain, string> =
  EXCEPTION_DOMAIN_ORDER.reduce(
    (acc, d) => {
      acc[d] = EXCEPTION_DOMAIN_META[d].label
      return acc
    },
    {} as Record<ExceptionDataDomain, string>,
  )

const LABEL_TO_DOMAIN: Record<string, ExceptionDataDomain> = Object.fromEntries(
  EXCEPTION_DOMAIN_ORDER.map((d) => [EXCEPTION_DOMAIN_META[d].label, d]),
)

export const getCategoryIcon = (
  category: string,
  opts?: { size?: number; applyColor?: boolean },
): React.ReactElement => {
  const domain = LABEL_TO_DOMAIN[category]
  if (domain)
    return (
      <ExceptionDomainIcon
        domain={domain}
        variant="outlined"
        size={opts?.size}
        applyColor={opts?.applyColor}
      />
    )
  return <FallbackIcon />
}

export const getCategoryColor = (
  _category: string,
): 'primary' | 'secondary' | 'success' | 'warning' | 'info' => 'primary'

export const rowSearchHaystack = (row: ReportType): string =>
  [row.name, row.description, row.category].filter(Boolean).join(' ').toLowerCase()

export const buildExceptionRows = (reportTypes: ReportType[]): ReportType[] => {
  const umbrella = reportTypes.find((r) => r.id === 'exceptions')
  const types = umbrella?.exception_types
  if (!types?.length) return []
  return types
    .map((t) => {
      const domain = t.data_domain as ExceptionDataDomain | undefined
      const category = domain ? DOMAIN_TO_GROUP[domain] : undefined
      if (!category) return null
      const domainLabel = domain ? EXCEPTION_DOMAIN_META[domain]?.label ?? '' : ''
      const description = t.description ?? ''
      const name = t.label
      const keywords = (t.search_keywords ?? []).join(' | ')
      const base: ReportType = {
        id: `exception-${t.key}`,
        name,
        description,
        category,
        url_slug: `exceptions?exceptionTypes=${t.key}`,
        is_exception_report: true,
        exception_type: t.exception_type ?? null,
        visible_to_roles: t.visible_to_roles ?? null,
      }
      base.searchHaystack = [name, description, category, domainLabel, keywords]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return base
    })
    .filter((r): r is ReportType => r !== null)
}

export const groupAndSort = (reports: ReportType[]): [string, ReportType[]][] => {
  const grouped = new Map<string, ReportType[]>()
  for (const report of reports) {
    const list = grouped.get(report.category) || []
    list.push(report)
    grouped.set(report.category, list)
  }
  return [...grouped.entries()].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a[0])
    const bi = CATEGORY_ORDER.indexOf(b[0])
    if (ai !== -1 && bi !== -1) return ai - bi
    if (ai !== -1) return -1
    if (bi !== -1) return 1
    return a[0].localeCompare(b[0])
  })
}
